/**
 * DeleteClientModal Component
 * Modal interface for confirming client deletion
 * Displays warning message and requires explicit confirmation
 */

import { useState, useEffect } from 'react'
import { X, AlertTriangle, Loader2 } from 'lucide-react'
import { removeClient, RevFinOpsClient } from '@/lib/revfinops-api'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface DeleteClientModalProps {
  client: RevFinOpsClient
  isOpen: boolean
  onClose: () => void
  onSuccess: (deletedClientId: string) => void
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * DeleteClientModal - Modal dialog for confirming client deletion
 *
 * Features:
 * - Modal overlay with backdrop blur
 * - Warning message with client name
 * - Prominent danger styling
 * - Permanent deletion warning
 * - API integration with removeClient
 * - Success/error handling
 * - Keyboard accessibility (ESC to close)
 * - Focus trap within modal
 * - Outside click to close
 * - Loading state during deletion
 *
 * @param client - Client object to delete
 * @param isOpen - Modal visibility state
 * @param onClose - Callback to close modal
 * @param onSuccess - Callback when client is successfully deleted
 */
export function DeleteClientModal({
  client,
  isOpen,
  onClose,
  onSuccess
}: DeleteClientModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handle confirm deletion - calls removeClient API
   */
  const handleConfirm = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await removeClient(client.id)

      if (response.success) {
        onSuccess(client.id)
        onClose()
      } else {
        setError(response.error?.message || 'Failed to delete client')
      }
    } catch (err) {
      setError('An error occurred while deleting the client')
      console.error('DeleteClientModal: Error deleting client:', err)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handle cancel - closes modal without deleting
   */
  const handleCancel = () => {
    if (!loading) {
      setError(null)
      onClose()
    }
  }

  /**
   * Handle overlay click - closes modal when clicking outside
   */
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) {
      handleCancel()
    }
  }

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Handle ESC key press to close modal
   */
  useEffect(() => {
    if (!isOpen) return

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        handleCancel()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [isOpen, loading])

  /**
   * Prevent body scroll when modal is open
   */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  /**
   * Clear error when modal closes
   */
  useEffect(() => {
    if (!isOpen) {
      setError(null)
    }
  }, [isOpen])

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!isOpen) return null

  return (
    <div
      className="modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-client-title"
    >
      <div className="modal-content max-w-md">
        {/* Modal Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-error/20 rounded-monumenta">
              <AlertTriangle className="h-6 w-6 text-error" />
            </div>
            <h2
              id="delete-client-title"
              className="font-display text-2xl font-bold text-white"
            >
              Delete Client
            </h2>
          </div>
          <button
            onClick={handleCancel}
            className="btn-ghost p-2"
            aria-label="Close modal"
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Warning Message */}
        <div className="space-y-4 mb-6">
          <p className="text-brand-gray-light font-body">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-white">{client.name}</span>?
          </p>

          <div className="card border-error/30 bg-error/10">
            <p className="text-error font-medium font-body">
              This action cannot be undone. The client and all associated data will be permanently deleted.
            </p>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="card border-error/30 bg-error/10 mb-4">
            <p className="text-error font-body">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="btn-danger"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Deleting...
              </>
            ) : (
              'Delete Client'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DeleteClientModal
